import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AUTH_ENDPOINTS } from '../constants/api';

type VerifyStatus = 'verifying' | 'success' | 'error' | 'invalid';

interface UseVerifyEmailResult {
  status: VerifyStatus;
  message: string;
  countdown: number;
  sessionToken: string | null;
  refreshToken: string | null;
}

const REDIRECT_COUNTDOWN_SECONDS = 5;

export const useVerifyEmail = (token: string | null, onVerified: () => void): UseVerifyEmailResult => {
  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [message, setMessage] = useState('Verifying your email address...');
  const [countdown, setCountdown] = useState(REDIRECT_COUNTDOWN_SECONDS);

  const hasVerifiedRef = useRef(false);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified; // always call the latest passed-in callback without retriggering the effect

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage('No verification token was provided. Please use the link from your email.');
      return;
    }

    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;

    const verify = async () => {
      try {
        const res = await axios.post(AUTH_ENDPOINTS.VERIFY_EMAIL, { token });
        setStatus('success');
        setMessage(res.data?.message || 'Your email has been verified successfully.');
        setSessionToken(res.data?.token ?? null);
        setRefreshToken(res.data?.refreshToken ?? null);
      } catch (err: any) {
        setStatus('error');
        setMessage(
          err.response?.data?.message ||
          'We could not verify your email. The link may be invalid or expired.'
        );
      }
    };

    verify();
  }, [token]);

  // Countdown + redirect, only once verification actually succeeds
  useEffect(() => {
    if (status !== 'success') return;

    if (countdown <= 0) {
      onVerifiedRef.current();
      return;
    }

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown]);

  return { status, message, countdown, sessionToken, refreshToken };
};
